import { fetchMyListings } from '../../../api/seller-listings'
import { apiUrl } from '../../../lib/api'
import { catalogMediaUrl, type CatalogProduct } from '../catalog/catalog-types'

export const sellerHomeKeys = {
  summary: ['seller', 'home', 'summary', 30] as const,
  summaryFor: (windowDays: number) => ['seller', 'home', 'summary', windowDays] as const,
  health: ['seller', 'stores', 'health'] as const,
  listings: ['seller', 'my-listings', 'home-facets'] as const,
  catalogue: ['seller', 'catalog', 'home-newest'] as const,
}

export type SellerHomeSummary = {
  window: {
    timezone: string
    start_date: string
    end_date: string
    days: number
  }
  orders: {
    total: number
    gross_sales_usd: number
    previous_total: number
    previous_gross_sales_usd: number
    by_state: Record<string, number>
    daily: Array<{ date: string; order_count: number; gross_sales_usd: number }>
    by_store: SellerHomeStoreSummary[]
  }
}

export type SellerHomeStoreSummary = {
  store_integration_id: string | null
  store_origin: string
  shop_name: string | null
  order_count: number
  gross_sales_usd: number
  completed_count: number
  cancelled_count: number
}

type SellerStoreHealth = {
  store_integration_id: string
  provider: string
  shop_name: string
  verdict: 'healthy' | 'degraded' | 'failing'
  active: boolean
  reasons: string[]
}

export type SellerStoreHealthResponse = {
  items: SellerStoreHealth[]
  total: number
  summary: { by_verdict: Record<string, number> }
}

type CatalogueResponse = {
  items: Array<{
    id: string
    title: string
    display_code?: string | null
    category: string
    subcategory_id?: string | null
    cover_image_url: string | null
    hover_image_url: string | null
    is_new: boolean
    is_favorite: boolean
    label: string | null
    price_usd: number | null
    card_facts?: string[]
  }>
  total: number
}

async function fetchJson<T>(path: string): Promise<T> {
  const response = await fetch(apiUrl(path))
  if (!response.ok) throw new SellerHomeHttpError(response.status)
  return response.json() as Promise<T>
}

export class SellerHomeHttpError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`HTTP ${status}`)
    this.name = 'SellerHomeHttpError'
    this.status = status
  }
}

export function fetchSellerHomeSummary(windowDays = 30): Promise<SellerHomeSummary> {
  return fetchJson(`/seller/home-summary?window_days=${windowDays}`)
}

export function fetchSellerHomeHealth(): Promise<SellerStoreHealthResponse> {
  return fetchJson('/store-integrations/health?limit=50')
}

export function fetchSellerHomeListings() {
  return fetchMyListings({ page: 1, per_page: 1 })
}

export async function fetchSellerHomeCatalogue(): Promise<CatalogProduct[]> {
  const response = await fetchJson<CatalogueResponse>(
    '/master-catalog?limit=3&offset=0&sort=newest',
  )
  return response.items.map((item) => ({
    id: item.id,
    slug: item.id,
    name: item.title,
    displayCode: item.display_code ?? null,
    category: item.category,
    subcategoryId: item.subcategory_id ?? null,
    priceUsd: item.price_usd,
    coverImageUrl: catalogMediaUrl(item.cover_image_url),
    hoverImageUrl: catalogMediaUrl(item.hover_image_url),
    isNew: item.is_new,
    isFavorite: item.is_favorite,
    label: item.label,
    cardFacts: item.card_facts ?? [],
  }))
}
